/**
 * Captures ONLY the visible viewport as a PNG blob (what the user currently
 * sees), excluding any element with `data-maua-feedback-ignore` (and the widget root).
 *
 * Uses `modern-screenshot`, which renders via an SVG <foreignObject> — the
 * browser paints the DOM, so modern CSS (oklch, backdrop-filter, transforms,
 * web fonts, gradients) renders correctly.
 *
 * SCROLL HANDLING — the hard part:
 *   modern-screenshot CLONES the DOM before rendering, and a cloned element
 *   resets to scrollTop = 0. So anything the user scrolled — the window, the
 *   <body>, OR a custom overflow container (Next.js/Nuxt SPAs scroll a div,
 *   not the window) — would otherwise render from the top, and the content
 *   actually on screen would be missing from the shot.
 *
 *   We enable the library's `restoreScrollPosition` feature, which walks the
 *   clone and bakes every scrolled ancestor's offset into a CSS transform
 *   (via DOMMatrix, preserving existing transforms, recursing through nested
 *   containers + shadow roots). The net effect: the clone looks pixel-identical
 *   to what's on screen, with the visible viewport pinned to the canvas origin.
 *   That's why we crop at (0, 0) and NOT at window.scrollY — the scroll is
 *   already accounted for in the render.
 *
 * FONTS — the other hard part:
 *   An SVG rendered as an <img> cannot load external resources, so every web
 *   font must be inlined as a data URI or the shot silently falls back to a
 *   system font. Different metrics = different layout = the spacing bug the
 *   reporter is pointing at no longer shows in the screenshot. The library only
 *   embeds fonts from script-readable stylesheets, which excludes every
 *   cross-origin font CSS (Google Fonts, Adobe Fonts, a CDN). `collectEmbeddedFontCss`
 *   fills that gap; see capture/fonts.ts. If it comes back empty we hand the
 *   job back to the library rather than shipping a partial font sheet.
 *
 * The library is dynamically imported so it's only fetched when the user opens
 * screenshot mode — keeps the initial widget bundle small.
 */
import { collectEmbeddedFontCss, waitForFontsReady } from "./fonts";

// iOS Safari (and some Android browsers) hard-cap canvas area at ~16.7M px².
// We stay well under it. Rendering only the viewport (not the full document)
// keeps us there on virtually any device; the clamp is a final safety net for
// large tablets in landscape.
const MAX_CANVAS_AREA = 8_000_000;

export async function captureScreenshot(rootToIgnore?: HTMLElement): Promise<Blob> {
  const { domToCanvas } = await import("modern-screenshot");

  // Fonts first: wait for any pending swap, then build a fully inlined
  // @font-face sheet. Both run before the clone so the render sees final metrics.
  await waitForFontsReady();
  const fontCssText = await collectEmbeddedFontCss();

  const width = window.innerWidth;
  const height = window.innerHeight;

  // Choose a render scale for crispness, but cap the total canvas area so we
  // never exceed the browser's canvas limit (the iOS "Load failed" cause when
  // the full document was rendered at 2× on a long page).
  let scale = Math.min(window.devicePixelRatio || 1, 2);
  if (width * height * scale * scale > MAX_CANVAS_AREA) {
    scale = Math.max(1, Math.sqrt(MAX_CANVAS_AREA / (width * height)));
  }

  // Pass width/height = viewport. modern-screenshot sizes the foreignObject to
  // these and CLIPS overflow (it does not scale), so only the viewport region
  // is rendered instead of the entire (possibly very tall) document. Combined
  // with restoreScrollPosition — which pins the visible content to the canvas
  // origin — this yields exactly the on-screen viewport at a bounded size.
  const canvas = await domToCanvas(document.documentElement, {
    width,
    height,
    scale,
    backgroundColor: null,
    // `font.cssText` is inlined VERBATIM by the library (it does not resolve
    // URLs), which is why fonts.ts hands back data URIs. Undefined = library default.
    font: fontCssText ? { cssText: fontCssText } : undefined,
    features: { restoreScrollPosition: true },
    filter: (node) => {
      if (!(node instanceof Element)) return true;
      if (rootToIgnore && (node === rootToIgnore || rootToIgnore.contains(node))) return false;
      return !node.hasAttribute?.("data-maua-feedback-ignore");
    },
  });

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to encode screenshot"));
          return;
        }
        resolve(blob);
      },
      "image/png",
      0.92,
    );
  });
}
